import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileText, Loader2, Mail, PiggyBank, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatErr } from "@/lib/api";

const usd = (n) => `$${Number(n || 0).toLocaleString()}`;
const range = (lo, hi) => (lo === hi ? usd(lo) : `${usd(lo)} – ${usd(hi)}`);

export function FundsGap({ prep, onRebuild }) {
  const f = prep.funds_plan;
  const st = prep.funds_status;
  const title = (id) => prep.grants.find((g) => g.grant_id === id)?.title || "";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="funds-gap">
      <div className="flex items-center gap-2"><PiggyBank className="h-5 w-5 text-emerald-600" /><h3 className="font-semibold text-slate-900 flex-1">Funds gap planner</h3>
        {st === "ready" && <button onClick={onRebuild} className="text-slate-400 hover:text-slate-900" aria-label="Recalculate" title="Recalculate" data-testid="funds-gap-rebuild"><RefreshCw className="h-4 w-4" /></button>}</div>
      {["waiting", "building"].includes(st) && <p className="text-sm text-slate-500 mt-3 flex items-center gap-2" data-testid="funds-gap-loading"><Loader2 className="h-4 w-4 animate-spin text-emerald-600" />{st === "waiting" ? "Adds up once every roadmap is ready." : "Adding up the money you'll need…"}</p>}
      {st === "error" && <p className="text-sm text-red-600 mt-3 flex items-center gap-2" data-testid="funds-gap-error"><AlertTriangle className="h-4 w-4" />Couldn't calculate. <button onClick={onRebuild} className="underline" data-testid="funds-gap-retry">Try again</button></p>}
      {st === "ready" && f && (
        <>
          <div className="mt-3 text-xs text-slate-500">Money to have on hand across all {prep.grants.length} grants</div>
          <div className="font-display text-3xl font-extrabold text-slate-900" data-testid="funds-gap-total">{range(f.low, f.high)}</div>
          <ul className="mt-3 space-y-1.5">{f.per_grant.map((x) => (
            <li key={x.grant_id} className="text-xs text-slate-600 flex gap-2" data-testid={`funds-gap-grant-${x.grant_id}`}><span className="font-semibold text-slate-900 whitespace-nowrap">{range(x.low, x.high)}</span><span className="min-w-0"><span className="line-clamp-1">{title(x.grant_id)}</span>{x.note && <span className="text-slate-400">{x.note}</span>}</span></li>))}</ul>
          {f.tips.length > 0 && <div className="mt-4 border-t border-slate-100 pt-3"><h4 className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-700 mb-2">Ways to cover it</h4>
            <ul className="space-y-2" data-testid="funds-gap-tips">{f.tips.map((t, i) => <li key={i} className="text-sm"><b className="text-slate-900">{t.title}.</b> <span className="text-slate-600">{t.detail}</span></li>)}</ul></div>}
          <p className="text-[11px] text-slate-400 mt-3">AI estimate, not financial advice. <Link to="/dashboard/capital" className="text-emerald-700 hover:underline" data-testid="funds-gap-capital-link">Find bridge funding in Capital Matchmaker</Link></p>
        </>
      )}
    </div>
  );
}

export function WriteAll({ prep, onDone }) {
  const [busy, setBusy] = useState(false);
  const missing = prep.grants.filter((g) => !g.proposal).length;
  if (!prep.can_write) return <Link to="/pricing" className="text-xs text-emerald-700 hover:underline" data-testid="write-all-upgrade">Write all proposals with Proposal Writer</Link>;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/grant-prep/proposals"); toast.success(`Writing ${data.started} first draft${data.started === 1 ? "" : "s"}, one after another. Each takes a few minutes.`); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" onClick={go} disabled={busy || !missing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="write-all-proposals">
      {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}{missing ? `Write all proposals (${missing})` : "All proposals started"}
    </Button>
  );
}

export function ProposalLink({ g }) {
  if (!g.proposal) return null;
  const busy = ["queued", "generating"].includes(g.proposal.status);
  return (
    <Link to={`/dashboard/proposals/${g.proposal.proposal_id}`} className="text-xs text-slate-700 hover:text-emerald-700 inline-flex items-center gap-1" data-testid={`prep-proposal-${g.grant_id}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}{g.proposal.status === "queued" ? "Proposal queued" : busy ? "Writing proposal" : "Open proposal"}
    </Link>
  );
}

export function DocUpload({ item, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  if (item.kind !== "document") return null;
  const up = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post(`/grant-prep/checklist/${item.key}/upload`, fd); toast.success(`Saved to your Document Vault. Counts for ${data.grants} grant${data.grants === 1 ? "" : "s"}.`); onUploaded(); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-2 ml-2 align-middle">
      {item.doc_name ? <span className="text-[11px] text-emerald-700" data-testid={`prep-doc-name-${item.key}`}>Uploaded: {item.doc_name}</span>
        : item.in_vault && <Link to="/dashboard/documents" className="text-[11px] text-emerald-700 hover:underline" data-testid={`prep-in-vault-${item.key}`}>Already in your vault</Link>}
      <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt,.csv" className="hidden" onChange={up} data-testid={`prep-upload-input-${item.key}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} className="text-[11px] font-semibold text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1" data-testid={`prep-upload-${item.key}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{item.doc_name ? "Replace" : "Upload"}</button>
    </span>
  );
}

export function WeeklyEmail({ prep, onChange }) {
  const toggle = (on) => api.put("/grant-prep/weekly-email", { on }).then(() => { onChange(on); toast.success(on ? "Weekly email on. It arrives Monday mornings." : "Weekly email off"); }).catch((e) => toast.error(formatErr(e)));
  const test = () => api.post("/grant-prep/weekly-email/test").then(() => toast.success("Sent. Check your inbox.")).catch((e) => toast.error(formatErr(e)));
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700 mt-4" data-testid="prep-weekly-email">
      <Mail className="h-4 w-4 text-emerald-600" />
      <label className="flex items-center gap-2"><Switch checked={prep.weekly_email} onCheckedChange={toggle} data-testid="prep-weekly-email-switch" />Monday progress email</label>
      <button onClick={test} className="text-xs text-emerald-700 hover:underline" data-testid="prep-weekly-email-test">Send me this week's now</button>
    </div>
  );
}
